import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Phone, MessageSquare, Mail, Loader2, Inbox, Lock,
  LogOut, CheckCircle2, Clock, CalendarDays,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sdp_admin_pw";

const DAMAGE_LABEL = { wind: "Wind", hail: "Hail", tree: "Tree", other: "Other" };

const ContactIcon = ({ pref }) => {
  if (pref === "call") return <Phone size={14} />;
  if (pref === "text") return <MessageSquare size={14} />;
  return <Mail size={14} />;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtSlotDate = (ymd) => {
  try {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return ymd; }
};

const PasswordGate = ({ onSuccess }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onSuccess(pw);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Wrong password" : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md bg-slate-900/70 border border-slate-800 p-10"
      >
        <div className="flex items-center gap-3 text-red-600 mb-6">
          <Lock size={20} />
          <span className="text-xs uppercase tracking-[0.3em]">Admin access</span>
        </div>
        <h1 className="font-display font-black uppercase text-3xl text-white tracking-tight">
          Enter password
        </h1>
        <p className="text-slate-400 text-sm mt-2 mb-6">
          Submissions are private. Authorized access only.
        </p>
        <input
          data-testid="admin-password"
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          className="w-full bg-slate-950 border border-slate-700 text-white px-4 py-3 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600"
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className="mt-4 w-full bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 disabled:opacity-60 px-8 py-4 inline-flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          {busy ? "Checking…" : "Unlock"}
        </button>
      </form>
    </main>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (password) => {
    const p = password ?? pw;
    if (!p) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/inspections`, { headers: { "X-Admin-Password": p } });
      setRows(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setPw("");
        toast.error("Session expired — please log in again");
      } else {
        setError("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { if (pw) load(pw); }, [pw, load]);

  const toggleContacted = async (id, next) => {
    try {
      await axios.patch(
        `${API}/inspections/${id}`,
        { contacted: next },
        { headers: { "X-Admin-Password": pw } },
      );
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, contacted: next } : r)));
      toast.success(next ? "Marked as contacted" : "Marked as pending");
    } catch {
      toast.error("Could not update");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPw("");
    setRows([]);
  };

  if (!pw) {
    return (
      <>
        <Header />
        <PasswordGate onSuccess={setPw} />
        <Footer />
      </>
    );
  }

  const pending = rows.filter((r) => !r.contacted).length;

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 min-h-[60vh]">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-3">
              Admin · Submission inbox
            </div>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white tracking-tight">
              Inspection requests
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              {rows.length} total · {pending} pending follow-up
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-btn"
              onClick={() => load()}
              className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-600 hover:text-red-600 px-5 py-3 inline-flex items-center gap-2 text-xs transition-colors"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-500 hover:text-red-500 px-5 py-3 inline-flex items-center gap-2 text-xs transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="animate-spin mr-3" /> Loading…
          </div>
        )}

        {!loading && error && (
          <div data-testid="error-state" className="border border-red-900 bg-red-950/40 text-red-200 p-6">
            {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div
            data-testid="empty-state"
            className="border border-slate-800 bg-slate-900/50 p-16 text-center"
          >
            <Inbox className="mx-auto text-slate-600" size={48} />
            <h3 className="font-display font-bold uppercase text-xl text-white mt-4">No submissions yet</h3>
            <p className="text-slate-500 text-sm mt-2">New inspection requests will appear here.</p>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div data-testid="submissions-list" className="grid lg:grid-cols-2 gap-4">
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid={`row-${r.id}`}
                className={`bg-slate-900/60 border p-6 transition-colors ${
                  r.contacted ? "border-slate-800 opacity-70" : "border-slate-800 hover:border-red-600/50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-xl text-white flex items-center gap-2">
                      {r.name}
                      {r.contacted && <CheckCircle2 size={16} className="text-green-500" />}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)}</div>
                  </div>
                  <Badge className="bg-red-600 text-white hover:bg-red-500 uppercase font-bold rounded-none">
                    {DAMAGE_LABEL[r.damage_type] || r.damage_type}
                  </Badge>
                </div>

                <div className="mt-5 space-y-2 text-sm">
                  <a href={`tel:${r.phone}`} className="flex items-center gap-2 text-slate-200 hover:text-red-600">
                    <Phone size={14} className="text-red-600" /> {r.phone}
                  </a>
                  <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-slate-200 hover:text-red-600 break-all">
                    <Mail size={14} className="text-red-600" /> {r.email}
                  </a>
                  <div className="text-slate-400 pt-1">{r.address}</div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
                  <ContactIcon pref={r.contact_preference} />
                  Prefers {r.contact_preference}
                </div>

                {r.availability?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-600 mb-2 flex items-center gap-2">
                      <CalendarDays size={12} /> Customer availability (2hr windows)
                    </div>
                    <div className="grid gap-2">
                      {r.availability.map((s, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm"
                        >
                          <span className="text-white font-bold">{fmtSlotDate(s.date)}</span>
                          <span className="text-red-600 inline-flex items-center gap-1">
                            <Clock size={12} /> {s.window}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.message ? (
                  <p className="text-slate-300 text-sm mt-4 leading-relaxed border-l-2 border-red-600 pl-4">
                    {r.message}
                  </p>
                ) : null}

                <button
                  data-testid={`toggle-contacted-${r.id}`}
                  onClick={() => toggleContacted(r.id, !r.contacted)}
                  className={`mt-5 w-full font-bold uppercase tracking-wide px-5 py-3 inline-flex items-center justify-center gap-2 text-xs transition-colors ${
                    r.contacted
                      ? "bg-transparent border-2 border-slate-700 text-slate-400 hover:border-red-600 hover:text-red-600"
                      : "bg-red-600 text-white hover:bg-red-500"
                  }`}
                >
                  <CheckCircle2 size={14} />
                  {r.contacted ? "Mark as pending" : "Mark as contacted"}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
